import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, Trash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface DeleteButton {
    id: string;
    deleteFn: (id: string) => Promise<any>;
    queryKey: string | string[];
    buttonSize?: "sm" | "lg" | "default" | "icon";
    buttonVariant?:
        | "destructive"
        | "default"
        | "outline"
        | "secondary"
        | "ghost"
        | "link";
    title?: string;
    description?: string;
    successTitle?: string;
    successDescription?: string;
    errorTitle?: string;
    errorDescription?: string;
    onSuccessCallback?: () => void;
    isInside?: boolean;
}

function DeleteButton({
    id,
    deleteFn,
    queryKey,
    buttonSize = "sm",
    buttonVariant = "destructive",
    title = "Delete Item",
    description = "Are you sure you want to delete this item? This action cannot be undone. Click delete to confirm.",
    successTitle = "Item Deleted Successfully",
    successDescription = "The operation was completed successfully.",
    errorTitle = "Oops Something went wrong",
    errorDescription = "While deleting the item encountered some issue",
    isInside = false,
    onSuccessCallback,
}: DeleteButton) {
    const queryClient = useQueryClient();
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();

    const mutation = useMutation({
        mutationFn: (id: string) => deleteFn(id),
        onError: (err) => {
            console.log({ err });
            toast.error(errorTitle, { description: errorDescription });
            setIsOpen(false);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: Array.isArray(queryKey) ? queryKey : [queryKey],
            });

            if (isInside) {
                navigate(-1);
            }
            toast.success(successTitle, { description: successDescription });

            setIsOpen(false);

            if (onSuccessCallback) {
                onSuccessCallback();
            }
        },
    });

    return (
        <Dialog open={isOpen} onOpenChange={(isOpen) => setIsOpen(isOpen)}>
            <DialogTrigger asChild>
                <Button
                    variant={isInside ? buttonVariant : "outline"}
                    size={buttonSize}
                    className="flex items-center gap-2"
                    onClick={() => setIsOpen(true)}
                >
                    <Trash className="h-4 w-4" />
                    {isInside && "Delete"}
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>{description}</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        className="bg-red-600 text-white hover:bg-red-700"
                        onClick={() => {
                            mutation.mutate(id);
                        }}
                        disabled={mutation.isPending}
                    >
                        {mutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            "Delete"
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default DeleteButton;
